/**
 * Upload files — one screen.
 *
 * Choose files, give the period, press "Upload n files". Each file goes
 * straight to storage on its own, so one failure never affects another, then
 * is read; its row shows progress and what it became (ready to process, or
 * needs mapping, with the link). Finished: back to the client's files, or
 * straight to mapping when exactly one file needs it.
 */

import { useModule } from '../modules/useModule';
import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { Box, Button, IconButton, LinearProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IngestProgress from './IngestProgress';
import { Card, ErrorNotice, Note, StatusBadge } from '../../shared/ui';
import { Field } from '../../shared/fields';
import { PageHeader } from '../../provider/pageTitle';
import { useClientCrumbs } from './crumbs';
import { useClientQuery, useUploadFileMutation } from '../../queries/intake';
import * as intakeApi from '../../api/Intake';
import { parsed } from '../../api/parse';
import { fileRecordSchema } from '../../schemas';
import {
	changeGroupMembers,
	useFileGroupQuery,
} from '../../queries/fileGroups';
import { keys } from '../../queries/keys';
import { bytes, count } from '../../utils/format';
import { createSerialQueue, withAdded } from '../fileGroups/membership';
import { LoadingState } from '../shared/states';
import type { FileRecord } from '../../schemas';

interface Chosen {
	id: string;
	file: File;
	name: string;
	sizeBytes: number;
}

type Phase = 'queued' | 'uploading' | 'reading' | 'done' | 'error';

export default function UploadWizard() {
	const { clientId = '' } = useParams();
	const navigate = useNavigate();
	const client = useClientQuery(clientId);
	const clientCrumbs = useClientCrumbs(clientId);
	const queryClient = useQueryClient();
	const upload = useUploadFileMutation();
	const { module, link } = useModule();

	const [periodLabel, setPeriodLabel] = useState('');
	const [chosen, setChosen] = useState<Chosen[]>([]);
	const [dragging, setDragging] = useState(false);
	const [progress, setProgress] = useState<Record<string, number>>({});
	const [phase, setPhase] = useState<Record<string, Phase>>({});
	const [failure, setFailure] = useState<Record<string, unknown>>({});
	const [results, setResults] = useState<Record<string, FileRecord>>({});
	const input = useRef<HTMLInputElement>(null);
	const seq = useRef(0);
	const back = `/intake/clients/${clientId}`;
	/* Every way out of this page stays in the module the files went into. */
	const go = (path: string) => navigate(link(path));
	/* Opened from a group: each uploaded file is added to it as soon as its
     record exists, one save at a time (the save replaces the whole set). */
	const [params] = useSearchParams();
	const groupId = params.get('group');
	const group = useFileGroupQuery(groupId, Boolean(groupId));
	const queue = useRef(createSerialQueue());
	const [grouping, setGrouping] = useState<{ added: number; error: unknown }>(
		{
			added: 0,
			error: null,
		},
	);
	const returnTo = groupId ? `${back}?group=${groupId}` : back;

	const add = (files: FileList | null) => {
		if (!files) return;
		const next: Chosen[] = Array.from(files).map((file) => {
			seq.current += 1;
			return {
				id: `c${seq.current}`,
				file,
				name: file.name,
				sizeBytes: file.size,
			};
		});
		setChosen((current) => [...current, ...next]);
	};

	const setPhaseOf = (id: string, at: Phase) =>
		setPhase((current) => ({ ...current, [id]: at }));

	/* Each file on its own call, so one failure never holds up another. */
	const uploadOne = async (item: Chosen) => {
		setFailure((current) => ({ ...current, [item.id]: undefined }));
		try {
			const record = await upload.mutateAsync({
				clientId,
				fileName: item.name,
				file: item.file,
				periodLabel: periodLabel.trim(),
				module,
				onPhase: (at) => setPhaseOf(item.id, at),
				onProgress: (pct) =>
					setProgress((current) => ({ ...current, [item.id]: pct })),
			});
			setResults((current) => ({ ...current, [item.id]: record }));
			if (groupId) {
				void queue.current
					.run(() =>
						changeGroupMembers(groupId, (current) =>
							withAdded(current, [record.pkFileId]),
						),
					)
					.then(
						() =>
							setGrouping((state) => ({
								...state,
								added: state.added + 1,
							})),
						(error: unknown) =>
							setGrouping((state) => ({ ...state, error })),
					)
					.finally(() => {
						void queryClient.invalidateQueries({
							queryKey:
								keys.fileGroups.forClientAnyModule(clientId),
						});
						void queryClient.invalidateQueries({
							queryKey: keys.fileGroups.detail(groupId),
						});
					});
			}
		} catch (error) {
			setPhaseOf(item.id, 'error');
			setFailure((current) => ({ ...current, [item.id]: error }));
		}
	};

	const uploadAll = () => {
		for (const item of chosen) {
			const at = phase[item.id];
			if (!at || at === 'error') void uploadOne(item);
		}
	};

	/* When reading settles, re-read the file for what it became. */
	const settle = useCallback(
		async (id: string, fileId: string, status: 'COMPLETED' | 'FAILED') => {
			setPhase((current) =>
				current[id] === 'reading'
					? {
							...current,
							[id]: status === 'COMPLETED' ? 'done' : 'error',
						}
					: current,
			);
			if (status === 'COMPLETED') {
				try {
					const fresh = parsed(
						fileRecordSchema,
						await intakeApi.getFile(fileId),
						`/files/${fileId}`,
					);
					setResults((current) => ({ ...current, [id]: fresh }));
				} catch {
					/* The row keeps its last known state; the file page has the rest. */
				}
			}
			void queryClient.invalidateQueries({ queryKey: keys.files.all });
			void queryClient.invalidateQueries({ queryKey: keys.clients.all });
		},
		[queryClient],
	);

	if (client.isPending) return <LoadingState label="Loading…" />;

	const started = chosen.some((item) => phase[item.id]);
	const busy = Object.values(phase).some(
		(at) => at === 'uploading' || at === 'reading',
	);
	const finished =
		chosen.length > 0 &&
		chosen.every(
			(item) => phase[item.id] === 'done' || phase[item.id] === 'error',
		);
	const needsMapping = Object.values(results).filter(
		(record) => record.status === 'MAPPING_REQUIRED',
	);
	const pending = chosen.filter(
		(item) => !phase[item.id] || phase[item.id] === 'error',
	);
	const periodMissing = periodLabel.trim().length === 0;

	return (
		<div className="stack wizard-page">
			<PageHeader
				breadcrumbs={[
					...clientCrumbs,
					{
						label: 'Upload',
						to: `/intake/clients/${clientId}/upload`,
					},
				]}
				title="Upload files"
				subtitle={group.data ? `Into ${group.data.name}` : undefined}
			/>

			<Card>
				<div className="stack">
					<div
						className={`dropzone${dragging ? ' on' : ''}`}
						onDragOver={(event) => {
							event.preventDefault();
							setDragging(true);
						}}
						onDragLeave={() => setDragging(false)}
						onDrop={(event) => {
							event.preventDefault();
							setDragging(false);
							add(event.dataTransfer.files);
						}}
					>
						<p>Drag the client's Excel or CSV exports here, or</p>
						<Button
							variant="outlined"
							size="small"
							onClick={() => input.current?.click()}
						>
							Choose files
						</Button>
						<input
							ref={input}
							type="file"
							multiple
							hidden
							accept=".xlsx,.xls,.csv,.txt"
							onChange={(event) => {
								add(event.target.files);
								event.target.value = '';
							}}
						/>
					</div>

					<div className="grid g3">
						<Field
							label="Period"
							required
							hint="As the client labels it, for example Jul 2026."
							error={
								started && periodMissing
									? 'Enter the period.'
									: undefined
							}
						>
							{(id) => (
								<input
									id={id}
									type="text"
									className="ctl"
									value={periodLabel}
									disabled={started && busy}
									placeholder="e.g. Jul 2026"
									onChange={(event) =>
										setPeriodLabel(event.target.value)
									}
								/>
							)}
						</Field>
					</div>

					{chosen.length === 0 ? (
						<p className="mut">No files chosen yet.</p>
					) : (
						<div className="up-panel">
							<div className="up-hd">
								<span className="b">
									{count(chosen.length)} file
									{chosen.length === 1 ? '' : 's'}
								</span>
							</div>
							<div className="up-list">
								{chosen.map((item) => {
									const at = phase[item.id];
									const record = results[item.id];
									return (
										<div className="up-item" key={item.id}>
											<div
												className="up-meta"
												style={{
													flex: '1 1 auto',
													minWidth: 0,
												}}
											>
												<div
													className="up-name"
													title={item.name}
												>
													{item.name}
												</div>
												<div className="small mut">
													{bytes(item.sizeBytes)}
													{at === 'uploading' &&
														` · Uploading… ${progress[item.id] ?? 0}%`}
													{at === 'reading' &&
														' · Reading the file…'}
													{at === 'done' &&
														record?.rowCount !==
															null &&
														record?.rowCount !==
															undefined &&
														` · ${count(record.rowCount)} rows, ${count(record.columnsTotal)} columns`}
												</div>
												{at === 'uploading' && (
													<LinearProgress
														variant="determinate"
														value={
															progress[item.id] ??
															0
														}
														sx={{ mt: 1 }}
													/>
												)}
												{record &&
													at !== 'uploading' && (
														<IngestProgress
															fileId={
																record.pkFileId
															}
															onSettled={(
																status,
															) =>
																void settle(
																	item.id,
																	record.pkFileId,
																	status,
																)
															}
														/>
													)}
												{record && at === 'done' && (
													<div
														className="row"
														style={{
															gap: 'var(--s3)',
															alignItems:
																'center',
															marginTop:
																'var(--s2)',
														}}
													>
														<StatusBadge
															group="fileStatus"
															value={
																record.status
															}
														/>
														{record.layoutMatch && (
															<StatusBadge
																group="layoutMatch"
																value={
																	record.layoutMatch
																}
																plain
															/>
														)}
														{record.status ===
															'MAPPING_REQUIRED' && (
															<Link
																to={link(
																	`${back}/files/${record.pkFileId}/mapping`,
																)}
																className="link nowrap"
															>
																Map columns
															</Link>
														)}
													</div>
												)}
												{at === 'error' && (
													<ErrorNotice
														error={
															failure[item.id] ??
															new Error(
																'Reading this file failed. Open it to see why.',
															)
														}
													/>
												)}
											</div>
											{!at && (
												<IconButton
													size="small"
													aria-label={`Remove ${item.name}`}
													onClick={() =>
														setChosen((current) =>
															current.filter(
																(other) =>
																	other.id !==
																	item.id,
															),
														)
													}
												>
													<CloseIcon fontSize="small" />
												</IconButton>
											)}
										</div>
									);
								})}
							</div>
						</div>
					)}

					{finished && needsMapping.length > 0 && (
						<Note
							tone="warn"
							row
							title={`${count(needsMapping.length)} need column mapping`}
						>
							Map their columns before they can be processed.
						</Note>
					)}
					{groupId && grouping.added > 0 && (
						<Note tone="info" row>
							Added {count(grouping.added)} to{' '}
							{group.data?.name ?? 'the group'}.
						</Note>
					)}
					{groupId && grouping.error !== null && (
						<Note
							tone="danger"
							row
							title={`Could not add to ${group.data?.name ?? 'the group'}`}
						>
							The files are uploaded; add them to the group from
							Data files.
						</Note>
					)}
				</div>
			</Card>

			<div className="wizard-foot">
				<div className="spread">
					<Button
						variant="outlined"
						disabled={busy}
						onClick={() => go(returnTo)}
					>
						{finished ? 'Back to Documents' : 'Cancel'}
					</Button>
					<Box display="flex" gap="var(--s2)">
						{pending.length > 0 && (
							<Button
								loading={busy}
								variant="contained"
								disabled={busy || periodMissing}
								title={
									periodMissing
										? 'Enter the period first.'
										: undefined
								}
								onClick={uploadAll}
							>
								{`Upload ${count(pending.length)} file${pending.length === 1 ? '' : 's'}`}
							</Button>
						)}
						{finished && pending.length === 0 && (
							<Button
								variant="contained"
								onClick={() =>
									go(
										needsMapping.length === 1
											? `${back}/files/${needsMapping[0]!.pkFileId}/mapping`
											: returnTo,
									)
								}
							>
								{needsMapping.length === 1
									? 'Map columns'
									: 'Done'}
							</Button>
						)}
					</Box>
				</div>
			</div>
		</div>
	);
}
